import { FC, ReactNode, Suspense, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import { CSSTransition } from 'react-transition-group';

import { CloseNewIcon } from 'components/Icon';

import styles from './BottomSheet.module.scss';

export type BottomSheetProps = Partial<Pick<HTMLDivElement, 'id' | 'className'>> & {
  title?: string;
  show?: boolean;
  fullscreen?: boolean;
  mountOnEnter?: boolean;
  unmountOnExit?: boolean;
  closeOnClickBackdrop?: boolean;
  blockBodyScroll?: boolean;
  withBackdrop?: boolean;
  renderHeader?(props: {
    headerClasses: string;
    titleClasses: string;
    closeButton: ReactNode;
    close: Function;
    title?: string;
  }): JSX.Element | null;
  onClose?(): void;
};

const transitionClassNames = {
  appear: styles.appear,
  appearActive: styles.appearActive,
  appearDone: styles.appearDone,
  enter: styles.enter,
  enterActive: styles.enterActive,
  enterDone: styles.enterDone,
  exit: styles.exit,
  exitActive: styles.exitActive,
  exitDone: styles.exitDone
};

const BottomSheet: FC<BottomSheetProps> = ({
  children,
  title,
  show = false,
  fullscreen = false,
  mountOnEnter = false,
  unmountOnExit = false,
  closeOnClickBackdrop = true,
  blockBodyScroll = true,
  withBackdrop = true,
  renderHeader,
  onClose,
  ...htmlProps
}): JSX.Element => {
  const transitionProps = useMemo(() => ({ in: show, classNames: transitionClassNames }), [show]);
  const wrapperTransition = useMemo(
    () => ({ ...transitionProps, timeout: { enter: 500, exit: 500, appear: 50 } }),
    [transitionProps]
  );
  const overlayTransition = useMemo(
    () => ({ ...transitionProps, timeout: { enter: 500, exit: 500, appear: 50 } }),
    [transitionProps]
  );
  const containerTransition = useMemo(
    () => ({ ...transitionProps, timeout: { enter: 500, exit: 500, appear: 50 } }),
    [transitionProps]
  );

  useEffect(() => {
    if (blockBodyScroll) {
      if (show) {
        document.documentElement.style.overflow = 'hidden';
      }

      return () => {
        document.documentElement.style.overflow = 'initial';
      };
    }

    return () => {};
  }, [show, blockBodyScroll]);

  const handleOnClose = () => {
    onClose?.();
  };

  const handleOnClickBackdrop = () => {
    if (closeOnClickBackdrop) {
      onClose?.();
    }
  };

  const renderDefaultHeader = () => (
    <div className={styles.header}>
      <span className={styles.title}>{title}</span>
      {closeButton}
    </div>
  );

  const closeButton = (
    <button type="button" className={styles.close} onClick={handleOnClose}>
      <CloseNewIcon />
    </button>
  );

  return createPortal(
    <Suspense fallback={null}>
      <CSSTransition
        data-id={htmlProps.id}
        className={cx(styles.wrapper, {
          [styles.fullscreen]: fullscreen,
          [styles.withBackdrop]: withBackdrop
        })}
        mountOnEnter={mountOnEnter}
        unmountOnExit={unmountOnExit}
        {...wrapperTransition}
      >
        <div>
          {withBackdrop && (
            <CSSTransition className={styles.overlay} appear {...overlayTransition} mountOnEnter unmountOnExit>
              <div aria-hidden onClick={handleOnClickBackdrop} />
            </CSSTransition>
          )}

          <CSSTransition
            className={cx(styles.container, htmlProps.className)}
            appear
            mountOnEnter={mountOnEnter}
            unmountOnExit={unmountOnExit}
            {...containerTransition}
          >
            <div>
              {renderHeader === undefined
                ? renderDefaultHeader()
                : renderHeader?.({
                    headerClasses: styles.header,
                    titleClasses: styles.title,
                    close: handleOnClose,
                    closeButton,
                    title
                  })}
              <div className={styles.content}>{children}</div>
              <div className={styles.footer} />
            </div>
          </CSSTransition>
        </div>
      </CSSTransition>
    </Suspense>,
    document.body
  );
};

export default BottomSheet;
